"use client";

import React, { useState, useMemo, useId } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Popover
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import moment from "moment-timezone";
import { useToast } from "@/utils/theme/useToast";

const TIMEZONE_NAMES = {
  "America/New_York": "Eastern Time",
  "America/Chicago": "Central Time",
  "America/Denver": "Mountain Time",
  "America/Los_Angeles": "Pacific Time",
  "America/Phoenix": "Mountain Standard Time",
  "America/Anchorage": "Alaska Time",
  "Pacific/Honolulu": "Hawaii Time",
  "Europe/London": "Greenwich Mean Time",
  "Europe/Paris": "Central European Time",
  "Europe/Berlin": "Central European Time",
  "Europe/Rome": "Central European Time",
  "Europe/Madrid": "Central European Time",
  "Europe/Amsterdam": "Central European Time",
  "Asia/Tokyo": "Japan Standard Time",
  "Asia/Shanghai": "China Standard Time",
  "Asia/Kolkata": "India Standard Time",
  "Asia/Dubai": "Gulf Standard Time",
  "Australia/Sydney": "Australian Eastern Time",
  "Australia/Melbourne": "Australian Eastern Time",
  "Australia/Perth": "Australian Western Time"
};

const formatTimezone = timezone => {
  const now = moment().tz(timezone);
  const offset = now.utcOffset();
  const absoluteOffset = Math.abs(offset);
  const hours = String(Math.floor(absoluteOffset / 60)).padStart(2, "0");
  const minutes = String(absoluteOffset % 60).padStart(2, "0");
  const offsetLabel = `UTC ${offset >= 0 ? "+" : "-"}${hours}:${minutes}`;
  const city = timezone
    .split("/")
    .pop()
    .replace(/_/g, " ");
  const name = TIMEZONE_NAMES[timezone] || city;
  const location = timezone.replace(/_/g, " ").replace(/\//g, " / ");
  return {
    value: timezone,
    name,
    location,
    offsetLabel,
    offset,
    label: `${name} (${offsetLabel})`,
    currentTime: now.format("HH:mm"),
    searchText: `${name} ${city} ${timezone} ${location} ${offsetLabel}`.toLowerCase()
  };
};

const filterTimezones = (options, { inputValue }) => {
  const search = inputValue.trim().toLowerCase();
  return options.filter(option => option.searchText.includes(search));
};

const inputSx = {
  "& .MuiInputBase-input": {
    // iOS zooms focused inputs below 16px, shifting the viewport sideways.
    fontSize: { xs: "16px", sm: "0.875rem" }
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    fontSize: "0.875rem",
    bgcolor: "action.hover",
    "& fieldset": { borderColor: "divider" },
    "&:hover fieldset": { borderColor: "text.secondary" },
    "&.Mui-focused fieldset": { borderColor: "text.primary" }
  }
};

const paperSx = {
  borderRadius: "16px",
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  backgroundImage: "none",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)"
};

const listboxSx = {
  maxHeight: 320,
  p: 0.75,
  overscrollBehavior: "contain",
  '& .MuiAutocomplete-option[aria-selected="true"]': {
    bgcolor: "action.selected",
    "&.Mui-focused": { bgcolor: "action.selected" }
  },
  "& .MuiAutocomplete-option.Mui-focused": { bgcolor: "action.hover" }
};

const TimezoneSelector = ({
  currentTimezone,
  onTimezoneChange,
  sx = {},
  disabled = false,
  variant = "select",
  open = false,
  autoFocus = false,
  onClose,
  triggerLabel
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const id = useId();
  const toast = useToast();
  const isValidTimezone = Boolean(
    currentTimezone && moment.tz.zone(currentTimezone)
  );
  const allTimezones = useMemo(() => {
    const names = moment.tz
      .names()
      .filter(
        timezone =>
          moment.tz.zone(timezone) &&
          !timezone.includes("SystemV") &&
          !timezone.startsWith("Etc/")
      );
    // Keep a valid stored zone selectable even when it is an excluded alias.
    if (isValidTimezone && !names.includes(currentTimezone)) {
      names.push(currentTimezone);
    }
    return names.map(formatTimezone).sort((a, b) => a.offset - b.offset);
  }, [currentTimezone, isValidTimezone]);
  const selectedTimezone =
    allTimezones.find(timezone => timezone.value === currentTimezone) ?? null;
  const displayLabel = selectedTimezone?.label || "Select time zone";

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
    onClose?.();
  };

  const selectTimezone = newTimezone => {
    if (disabled || !newTimezone) return;
    try {
      onTimezoneChange(newTimezone.value);
      handleClose();
    } catch (error) {
      console.error("Error changing timezone:", error);
      toast.show(
        "Timezone Error",
        "Unable to change timezone. Please try a different selection."
      );
    }
  };

  React.useEffect(() => {
    if (currentTimezone && !isValidTimezone) {
      toast.show(
        "Invalid Timezone",
        "The current timezone is not recognized. Please select a valid timezone."
      );
    }
  }, [currentTimezone, isValidTimezone, toast]);

  const renderOption = ({ key, ...props }, option) => (
    <Box
      key={option.value}
      component="li"
      {...props}
      sx={{
        gap: 1.5,
        minHeight: 64,
        borderRadius: "10px",
        px: 1.5,
        py: 1,
        color: "text.primary"
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
          {option.name}
        </Typography>
        <Typography
          sx={{ mt: 0.25, fontSize: "0.75rem", color: "text.secondary", overflowWrap: "anywhere" }}
        >
          {option.location}
        </Typography>
      </Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          {option.offsetLabel}
        </Typography>
        <Typography
          sx={{ mt: 0.25, fontSize: "0.75rem", color: "text.secondary" }}
        >
          {option.currentTime}
        </Typography>
      </Box>
      <Box sx={{ width: 18, display: "flex", flexShrink: 0 }}>
        {option.value === currentTimezone && (
          <CheckIcon sx={{ fontSize: 18 }} aria-hidden="true" />
        )}
      </Box>
    </Box>
  );

  const autocompleteProps = {
    options: allTimezones,
    value: selectedTimezone,
    getOptionLabel: option => option.label,
    getOptionKey: option => option.value,
    isOptionEqualToValue: (option, value) => option.value === value.value,
    filterOptions: filterTimezones,
    onChange: (_, newValue) => selectTimezone(newValue),
    disabled,
    disableClearable: true,
    renderOption,
    noOptionsText: "No time zones found. Try a city or UTC offset."
  };

  if (variant === "display") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          minWidth: 0,
          ...sx
        }}
      >
        <AccessTimeIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <Typography
          title={displayLabel}
          sx={{
            fontSize: "0.85rem",
            fontWeight: 600,
            color: disabled ? "text.disabled" : "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {displayLabel}
        </Typography>
      </Box>
    );
  }

  if (variant === "autocomplete") {
    return (
      <Autocomplete
        {...autocompleteProps}
        open={open}
        onClose={(_, reason) => {
          if (reason !== "selectOption") handleClose();
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Timezone"
            size="small"
            placeholder="Search city or time zone"
            autoFocus={autoFocus}
            sx={inputSx}
          />
        )}
        slotProps={{
          paper: { sx: { ...paperSx, mt: 0.5 } },
          listbox: { sx: listboxSx }
        }}
        sx={{ width: { xs: "100%", md: 280 }, ...sx }}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, ...sx }}>
      <Button
        disabled={disabled}
        onClick={event => setAnchorEl(event.currentTarget)}
        aria-label={`Change time zone, ${displayLabel}`}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={anchorEl ? id : undefined}
        title={displayLabel}
        startIcon={
          !triggerLabel && (
            <AccessTimeIcon sx={{ fontSize: "18px !important" }} />
          )
        }
        endIcon={
          !triggerLabel && (
            <KeyboardArrowDownIcon sx={{ fontSize: "18px !important" }} />
          )
        }
        sx={{
          minHeight: 40,
          minWidth: 0,
          maxWidth: "100%",
          px: 1.25,
          borderRadius: "10px",
          textTransform: "none",
          fontSize: "0.85rem",
          fontWeight: 600,
          color: "text.primary",
          "&:hover": { bgcolor: "action.hover" },
          "&.Mui-focusVisible": {
            outline: "2px solid",
            outlineColor: "text.primary",
            outlineOffset: 2
          },
          "& .MuiButton-icon": { color: "text.secondary" }
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {triggerLabel || displayLabel}
        </Box>
      </Button>
      <Popover
        open={Boolean(anchorEl) && !disabled}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        marginThreshold={16}
        slotProps={{
          paper: {
            id,
            role: "dialog",
            "aria-label": "Choose time zone",
            sx: {
              ...paperSx,
              mt: 0.75,
              width: "min(380px, calc(100vw - 32px))",
              maxWidth: "calc(100vw - 32px)",
              boxSizing: "border-box",
              maxHeight: "calc(100dvh - 32px)",
              overflow: "hidden"
            }
          }
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
            Time zone
          </Typography>
          <Typography
            sx={{ mt: 0.25, fontSize: "0.8rem", color: "text.secondary" }}
          >
            Search by city, region or UTC offset.
          </Typography>
        </Box>
        <Autocomplete
          {...autocompleteProps}
          open
          disablePortal
          forcePopupIcon={false}
          onClose={(_, reason) => {
            if (reason === "escape") handleClose();
          }}
          inputValue={searchTerm}
          onInputChange={(_, value, reason) => {
            if (reason === "input" || reason === "clear") setSearchTerm(value);
          }}
          renderInput={params => (
            <TextField
              {...params}
              placeholder="Search city or time zone"
              autoFocus
              size="small"
              sx={{ ...inputSx, px: 1.5, pb: 1 }}
              slotProps={{
                ...params.slotProps,
                htmlInput: {
                  ...params.slotProps.htmlInput,
                  "aria-label": "Search time zones"
                },
                input: {
                  ...params.slotProps.input,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ fontSize: 18, color: "text.secondary" }}
                      />
                    </InputAdornment>
                  )
                }
              }}
            />
          )}
          slotProps={{
            popper: {
              sx: {
                // Keep the searchable list inside the popover's focus trap.
                position: "static !important",
                transform: "none !important",
                width: "100% !important"
              },
              modifiers: [{ name: "flip", enabled: false }]
            },
            paper: {
              elevation: 0,
              sx: { borderRadius: 0, bgcolor: "background.paper" }
            },
            listbox: { sx: listboxSx }
          }}
        />
      </Popover>
    </Box>
  );
};

export default TimezoneSelector;
